import React, { useMemo } from 'react';
import './StatusSparklineAtom.scss';

export interface StatusSparklineColorEntry {
  fill: string;
  border?: string;
}

export interface StatusSparklineAtomProps {
  /** Status IDs in order (oldest → newest). Use `null` for empty slots. */
  values: Array<string | null>;
  /** Map from status ID to fill color token. Unknown IDs render as neutral. */
  colorMap: Record<string, StatusSparklineColorEntry>;
  /** Total slots to display. Values are right-aligned; missing slots get empty treatment. */
  slots?: number;
  /** Size of each square in px. */
  size?: number;
  /** Gap between squares in px. */
  gap?: number;
  /** Optional aria-label (defaults to "History: N filled of M slots"). */
  ariaLabel?: string;
  /** Optional tooltip (hoverable title). */
  tooltip?: string;
  /**
   * When supplied, the sparkline becomes an interactive button:
   * `role="button"`, focusable via Tab, activatable via click / Enter / Space.
   * When omitted, the sparkline stays as a passive `role="img"` display.
   */
  onClick?: () => void;
  className?: string;
}

function padAndTrim(values: Array<string | null>, slots: number): Array<string | null> {
  if (values.length >= slots) {
    return values.slice(values.length - slots);
  }
  const padding: Array<string | null> = new Array(slots - values.length).fill(null);
  return padding.concat(values);
}

const StatusSparklineAtom: React.FC<StatusSparklineAtomProps> = ({
  values,
  colorMap,
  slots = 12,
  size = 9,
  gap = 2,
  ariaLabel,
  tooltip,
  onClick,
  className = '',
}) => {
  const aligned = useMemo(() => padAndTrim(values, slots), [values, slots]);

  const filledCount = useMemo(
    () => aligned.reduce((count, v) => (v == null ? count : count + 1), 0),
    [aligned]
  );

  const resolvedAriaLabel =
    ariaLabel ?? `History: ${filledCount} filled of ${slots} slots`;

  const isInteractive = typeof onClick === 'function';

  const classes = [
    'status-sparkline-atom',
    isInteractive ? 'status-sparkline-atom--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (!isInteractive) return;
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <span
      className={classes}
      role={isInteractive ? 'button' : 'img'}
      aria-label={resolvedAriaLabel}
      title={tooltip}
      style={{ gap: `${gap}px` }}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
    >
      {aligned.map((value, index) => {
        const isEmpty = value == null;
        const entry = isEmpty ? undefined : colorMap[value as string];
        const isKnown = Boolean(entry);
        const slotClass = [
          'status-sparkline-atom__slot',
          isEmpty
            ? 'status-sparkline-atom__slot--empty'
            : isKnown
              ? 'status-sparkline-atom__slot--filled'
              : 'status-sparkline-atom__slot--unknown',
        ].join(' ');

        const style: React.CSSProperties = {
          width: `${size}px`,
          height: `${size}px`,
        };
        if (!isEmpty && isKnown && entry) {
          style.backgroundColor = entry.fill;
          if (entry.border) {
            style.border = `1px solid ${entry.border}`;
          }
        }

        return <span key={`slot-${index}-${value ?? 'empty'}`} className={slotClass} style={style} aria-hidden="true" />;
      })}
    </span>
  );
};

StatusSparklineAtom.displayName = 'StatusSparklineAtom';

export default StatusSparklineAtom;
